/**
 * The quotation's payment terms — a collapsible card beside Terms and Conditions (Akhil, 7).
 *
 * Its own card rather than a field inside `TermsCard`: the T&C card is a text editor for a
 * template's wording, while this is one pick from `lookups.get_payment_terms` — the ERP's
 * payment-terms master, whose names ARE the wording ("100% Advance along with Order"). The
 * pick is not a form field: it is the sentence the customer reads, sent in the email body
 * (`lib/quotationSendNote.ts`) and shown on the client view.
 *
 * Readable while closed, for the same reason `TermsCard` is: payment terms go to the customer,
 * and a collapsed card that says nothing is how a quotation goes out under the wrong ones.
 *
 * Editable whatever the quotation's status — the same rule `TermsCard` follows. It shipped
 * locked to drafts (2026-09-14), which on a sent quotation rendered a greyed, empty field with
 * nothing saying why; a revision changes terms as readily as prices.
 */

import { useState } from 'react'
import { Card } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { describePaymentTerm, usePaymentTerms, usePaymentTermsPicker } from '../../lib/api/usePaymentTerms'

export function PaymentTermsCard({
  value,
  saving,
  onCommit,
}: {
  /** The `Payment Terms Template` docname on the quotation, `''` when none. */
  value: string
  saving: boolean
  onCommit: (paymentTermsTemplate: string) => void
}) {
  const [open, setOpen] = useState(false)
  const terms = usePaymentTerms()
  const chosen = terms.data?.find((t) => t.name === value)
  const meaning = chosen ? describePaymentTerm(chosen) : ''

  return (
    <Card style={{ marginBottom: 14, ...(saving ? { opacity: 0.6 } : {}) }}>
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      >
        <b>💳 Payment terms</b>
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>
          {value ? (
            <>
              <b style={{ color: 'var(--ink)' }}>{value}</b>
              {meaning ? ` · ${meaning}` : ''}
            </>
          ) : (
            <span style={{ color: 'var(--warn)' }}>none set</span>
          )}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
      </div>

      {open ? (
        <div style={{ marginTop: 12 }}>
          <label className="ef" style={{ maxWidth: 380 }}>
            <span>Payment terms</span>
            <LinkCombo
              value={value}
              placeholder="Search payment terms…"
              useOptions={usePaymentTermsPicker}
              onChange={(name) => onCommit(name)}
            />
          </label>
          {/*
            What the customer will actually read (Akhil, 2026-09-15: "its terms message which
            will be passed with the quotation when sending"). The chosen wording goes into the
            email body when the quotation is sent — `quotationSendNote` — and onto the client
            view; this shows that sentence, so what is picked here is checked as it will be read.
          */}
          {value ? (
            <div style={{ fontSize: 12, marginTop: 8 }}>
              <div style={{ color: 'var(--muted)', fontSize: 11 }}>Sent to the customer as</div>
              <div style={{ fontWeight: 600 }}>Payment terms: {value}</div>
              {meaning ? <div style={{ color: 'var(--muted)' }}>{meaning}</div> : null}
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
              Nothing about payment is sent until one is chosen.
            </div>
          )}
        </div>
      ) : null}
    </Card>
  )
}
